import React, { useCallback, useState } from 'react';

import endOfMonth from 'date-fns/endOfMonth';
import endOfQuarter from 'date-fns/endOfQuarter';
import parseISO from 'date-fns/parseISO';
import startOfMonth from 'date-fns/startOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import { defineMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import { formatIsoLocalDate } from '@atlassian/townsquare-date-utils/date-format';

import { DateRangeScale, getDateRangeScale, useDateLocale } from '../../common/utils/utils';
import {
	ChangeSource,
	type DatePickerValue,
	type IntervalOption,
	type PickerProps,
} from '../../types';
import { DayPicker } from './day-picker';
import { IntervalPicker } from './interval-picker';
import { MonthPicker } from './month-picker';
import { QuarterPicker } from './quarter-picker';

const quarterPickerButtonMessage = defineMessage({
	id: 'date-range-picker.date-range-picker-content.quarter-picker-button-message',
	defaultMessage: 'Quarter',
	description: 'Button message for selecting quarter view of the calendar',
});

const monthPickerButtonMessage = defineMessage({
	id: 'date-range-picker.date-range-picker-content.month-picker-button-message',
	defaultMessage: 'Month',
	description: 'Button message for selecting month view of the calendar',
});

const dayPickerButtonMessage = defineMessage({
	id: 'date-range-picker.date-range-picker-content.day-picker-button-message',
	defaultMessage: 'Day',
	description: 'Button message for selecting day view of the calendar',
});

const intervalPickerButtonMessage = defineMessage({
	id: 'date-range-picker.date-range-picker-content.interval-picker-button-message',
	defaultMessage: 'Interval',
	description: 'Button message for selecting interval view of the calendar',
});

const buttonGroupStyles = xcss({
	padding: 'space.250',
	display: 'grid',
	gap: 'space.050',
	gridTemplateColumns: 'repeat(3, 1fr)',
	justifyContent: 'center',
});

const datePickerContentWrapperStyles = xcss({
	width: '322px',
});

const DEFAULT_GRANULARITIES = [
	DateRangeScale.DAY,
	DateRangeScale.MONTH,
	DateRangeScale.QUARTER,
] as const;

const getDateValueForScale = (
	endDateIso: string,
	newScale: DateRangeScale,
	locale: Locale,
): DatePickerValue | undefined => {
	const endDate = parseISO(endDateIso);

	switch (newScale) {
		case DateRangeScale.DAY:
			return { start: endDateIso, end: endDateIso };
		case DateRangeScale.MONTH: {
			const start = formatIsoLocalDate(startOfMonth(endDate), locale);
			const end = formatIsoLocalDate(endOfMonth(endDate), locale);
			return { start, end };
		}
		case DateRangeScale.QUARTER: {
			const start = formatIsoLocalDate(startOfQuarter(endDate), locale);
			const end = formatIsoLocalDate(endOfQuarter(endDate), locale);
			return { start, end };
		}
		default:
			return undefined;
	}
};

const DateRangePickerContent = ({
	withTimeScale = true,
	defaultRangeScale = DateRangeScale.MONTH,
	minDate,
	maxDate,
	granularities,
	intervals,
	isRangeSelect,
	...props
}: PickerProps): React.JSX.Element => {
	const { value: date } = props;
	const { previousValue: previousDate } = props;
	const { formatMessage } = useIntl();
	const locale = useDateLocale();

	const availableGranularities: DateRangeScale[] = granularities ?? [...DEFAULT_GRANULARITIES];

	const getInitialScale = () => {
		if (date) {
			const detectedScale = getDateRangeScale(date);
			return availableGranularities.includes(detectedScale)
				? detectedScale
				: availableGranularities[0];
		}
		return availableGranularities.includes(defaultRangeScale)
			? defaultRangeScale
			: availableGranularities[0];
	};

	const previousDateScale = previousDate ? getDateRangeScale(previousDate) : undefined;

	const [scale, setScale] = useState(getInitialScale());

	const onChange = useCallback(
		(newDate?: DatePickerValue, selectedInterval?: IntervalOption) => {
			if (newDate) {
				props.onChange(newDate, scale, selectedInterval, ChangeSource.DATE_SELECT);
			}
		},
		[props, scale],
	);

	const onScaleChange = useCallback(
		(newScale: DateRangeScale) => {
			setScale(newScale);
			if (date) {
				const newValue = getDateValueForScale(date.end, newScale, locale);
				if (newValue) {
					props.onChange(newValue, newScale, undefined, ChangeSource.SCALE_SELECT);
				}
			}
		},
		[date, locale, props],
	);

	const dateValue = date ? date.end : undefined;
	const previousDateValue = previousDate ? previousDate.end : undefined;

	const shouldShowGranularity = (granularity: DateRangeScale) =>
		availableGranularities.includes(granularity);

	const shouldShowSwitcher = withTimeScale && availableGranularities.length > 1;

	return (
		<Box xcss={datePickerContentWrapperStyles}>
			<Box testId="date-picker-button-wrapper" role="tablist">
				{shouldShowSwitcher && (
					<Box xcss={buttonGroupStyles}>
						{shouldShowGranularity(DateRangeScale.DAY) && (
							<Button
								isSelected={scale === DateRangeScale.DAY}
								onClick={() => onScaleChange(DateRangeScale.DAY)}
								testId="day-picker-button"
								aria-label={DateRangeScale.DAY}
								aria-selected={scale === DateRangeScale.DAY}
								role="tab"
							>
								{formatMessage(dayPickerButtonMessage)}
							</Button>
						)}
						{shouldShowGranularity(DateRangeScale.MONTH) && (
							<Button
								isSelected={scale === DateRangeScale.MONTH}
								onClick={() => onScaleChange(DateRangeScale.MONTH)}
								testId="month-picker-button"
								aria-label={DateRangeScale.MONTH}
								aria-selected={scale === DateRangeScale.MONTH}
								role="tab"
							>
								{formatMessage(monthPickerButtonMessage)}
							</Button>
						)}
						{shouldShowGranularity(DateRangeScale.QUARTER) && (
							<Button
								isSelected={scale === DateRangeScale.QUARTER}
								onClick={() => onScaleChange(DateRangeScale.QUARTER)}
								testId="quarter-picker-button"
								aria-label={DateRangeScale.QUARTER}
								aria-selected={scale === DateRangeScale.QUARTER}
								role="tab"
							>
								{formatMessage(quarterPickerButtonMessage)}
							</Button>
						)}
						{shouldShowGranularity(DateRangeScale.INTERVAL) && (
							<Button
								isSelected={scale === DateRangeScale.INTERVAL}
								onClick={() => onScaleChange(DateRangeScale.INTERVAL)}
								testId="interval-picker-button"
								aria-label={DateRangeScale.INTERVAL}
								aria-selected={scale === DateRangeScale.INTERVAL}
								role="tab"
							>
								{formatMessage(intervalPickerButtonMessage)}
							</Button>
						)}
					</Box>
				)}
			</Box>
			<Box testId="date-picker-content-wrapper">
				{scale === DateRangeScale.DAY && (
					<DayPicker
						date={dateValue}
						previousDate={previousDateScale === DateRangeScale.DAY ? previousDateValue : undefined}
						onSelect={onChange}
						minDate={minDate}
						maxDate={maxDate}
					/>
				)}
				{scale === DateRangeScale.MONTH && (
					<MonthPicker
						date={dateValue}
						previousDate={
							previousDateScale === DateRangeScale.MONTH ? previousDateValue : undefined
						}
						onSelect={onChange}
						withTimeScale={withTimeScale}
						minDate={minDate}
						maxDate={maxDate}
					/>
				)}
				{scale === DateRangeScale.QUARTER && (
					<QuarterPicker
						date={dateValue}
						previousDate={
							previousDateScale === DateRangeScale.QUARTER ? previousDateValue : undefined
						}
						onSelect={onChange}
						minDate={minDate}
						maxDate={maxDate}
					/>
				)}
				{scale === DateRangeScale.INTERVAL && intervals && (
					<IntervalPicker
						date={date}
						onSelect={(newDate, selectedInterval) => newDate && onChange(newDate, selectedInterval)}
						minDate={minDate}
						maxDate={maxDate}
						intervals={intervals}
						isRangeSelect={isRangeSelect}
					/>
				)}
			</Box>
		</Box>
	);
};

export default DateRangePickerContent;
